import { useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { Manulife as ManulifeEn, ManulifeFr } from '@manulife/mux-logos';
import { BREAKPOINTS, colors } from '@manulife/mux';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { APP_URL } from '../../utils/AppConstants';

const LogoRoot = styled.div`
    height: 80px;
    transition: opacity 0.5s;
    opacity: ${({ isScrolled }) => (isScrolled ? '0' : '1')};

    .logoInnerWrapper {
        display: flex;
        flex-direction: row;
        position: fixed;
        top: ${({ pageFrameHidden }) => (pageFrameHidden ? '0px' : '40px')};
        left: ${({ pageFrameHidden }) => (pageFrameHidden ? '0px' : '80px')};
    }

    @media (max-width: ${BREAKPOINTS.MAX_1024}px) {
        display: none;
        height: '0px';
        .logoInnerWrapper {
            top: 60px;
            left: 0px;
            width: 100%;
        }
    }

    @media print {
        display: none;
    }
`;

const Divider = styled.div`
    border-left: 2px solid ${colors.dark_3_light_grey};
    max-height: 50px;
    margin-top: 15px;
    @media (max-width: ${BREAKPOINTS.MAX_768}px) {
        display: none;
    }
`;

const CoBrandLogoWrapper = styled.div`
    display: flex;
    align-items: center;
    margin: 0px 30px;
`;

const WrappedLogo = ({ pageContainerRef }) => {
    const { language, coBrandingContent, isOtip, ctx } = useShallowEqualSelector((state) => ({
        language: state.Authentication.language,
        coBrandingContent: state.AEMContent.data?.['gb-client_cobranding'],
        isOtip: state.Context.isOtip,
        ctx: state.Context.ctx,
    }));
    const [isScrolled, setIsScrolled] = useState(false);
    const coBrandingPath = coBrandingContent?.logoHorizontalLightMode?.replace('/content/dam/groupbenefits/', '');

    /**
     * Tracking how far down the page the user is so we can fade the logo as the user scrolls from the top
     */
    const onScroll = useCallback(
        (e) => {
            if (e.target.scrollTop > 10 && !isScrolled) {
                setIsScrolled(true);
            } else if (e.target.scrollTop <= 10 && isScrolled) {
                setIsScrolled(false);
            }
        },
        [isScrolled, setIsScrolled]
    );

    /**
     * Registering scroll event for logo fading
     */
    useEffect(() => {
        const savedRef = pageContainerRef.current;

        if (savedRef) {
            savedRef.addEventListener('scroll', onScroll);
            //On component dismount, event listener is removed.
            return () => {
                savedRef.removeEventListener('scroll', onScroll);
            };
        }
    }, [pageContainerRef, onScroll]);

    const coBrandLogoHeight = isOtip ? '70px' : '25px';
    const pageFrameHidden = isOtip && ctx?.origin === 'mobile';
    const showManulifeBrand = !isOtip;

    return (
        <LogoRoot isScrolled={isScrolled} pageFrameHidden={pageFrameHidden} data-testid="logoRoot">
            <div className="logoInnerWrapper">
                {showManulifeBrand && GetHeaderLogo(language)}
                {coBrandingPath && (
                    <>
                        {showManulifeBrand && <Divider />}
                        <CoBrandLogoWrapper>
                            <img
                                id="cobrand-logo"
                                src={`${APP_URL.GB_BFF_HOST}${APP_URL.AEM_IMG_PATH}${coBrandingPath}`}
                                height={coBrandLogoHeight}
                                alt={coBrandingContent.logoAltText}
                            />
                        </CoBrandLogoWrapper>
                    </>
                )}
            </div>
        </LogoRoot>
    );
};

/**
 * Display English/French version of Manulife logo based on language environment
 * @param {string} language
 */
const GetHeaderLogo = (language) => {
    const styles = {
        width: '114px',
        height: '30px',
        margin: '25px 30px',
    };

    return (
        <>
            {language === 'fr-CA' ? (
                <ManulifeFr.WORDMARK_BLACK title="Manuvie" id="manulife-logo-top" style={styles} />
            ) : (
                <ManulifeEn.WORDMARK_BLACK title="Manulife" id="manulife-logo-top" style={styles} />
            )}
        </>
    );
};

export default WrappedLogo;
